import React, { useEffect, useMemo, useState } from "react";
import { api, downloadFile, formatINR, todayISO } from "@/lib/api";
import StatCard from "@/components/StatCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Receipt, Search, Download } from "lucide-react";
import { toast } from "sonner";
import PeriodSelector, { rangeFromValue } from "@/components/PeriodSelector";

export default function Expenses() {
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("__all");
  const [period, setPeriod] = useState({ mode: "last", n: 3 });
  const [entries, setEntries] = useState([]);
  const [search, setSearch] = useState("");

  const { start, end } = useMemo(() => rangeFromValue(period), [period]);

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data));
  }, []);

  useEffect(() => {
    const params = { start, end };
    if (vehicleId !== "__all") params.vehicle_id = vehicleId;
    api.get("/entries", { params }).then((r) => setEntries(r.data));
  }, [vehicleId, start, end]);

  const vehicleMap = useMemo(() => {
    const m = {};
    vehicles.forEach((v) => (m[v.id] = v));
    return m;
  }, [vehicles]);

  const rows = useMemo(() => {
    const out = [];
    for (const e of entries) {
      const v = vehicleMap[e.vehicle_id];
      for (const exp of e.expenses || []) {
        if ((exp.amount || 0) === 0 && !exp.remark) continue;
        const remark =
          (exp.remark && exp.remark.trim()) ||
          (e.expense_remark && e.expense_remark.trim()) ||
          (e.remark && e.remark.trim()) ||
          "";
        out.push({
          date: e.date,
          vehicleName: v?.name || "—",
          vehicleReg: v?.registration_number || "",
          amount: exp.amount || 0,
          remark,
        });
      }
    }
    out.sort((a, b) => (a.date < b.date ? 1 : -1));
    return out;
  }, [entries, vehicleMap]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.remark.toLowerCase().includes(q) ||
        r.vehicleName.toLowerCase().includes(q) ||
        r.date.includes(q)
    );
  }, [rows, search]);

  const total = filtered.reduce((a, b) => a + (b.amount || 0), 0);
  const count = filtered.length;
  const byCategory = useMemo(() => {
    const map = {};
    filtered.forEach((r) => {
      const key = (r.remark || "Uncategorised").trim().toLowerCase();
      const display = (r.remark || "Uncategorised").trim();
      if (!map[key]) map[key] = { label: display, total: 0, count: 0 };
      map[key].total += r.amount;
      map[key].count += 1;
    });
    return Object.values(map).sort((a, b) => b.total - a.total).slice(0, 6);
  }, [filtered]);

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="label-eyebrow">Outflow</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-1">
            Expenses
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Itemised expenses across all daily entries.
          </p>
        </div>
        <Button
          onClick={async () => {
            try {
              const params = new URLSearchParams({ start, end });
              if (vehicleId !== "__all") params.set("vehicle_id", vehicleId);
              const v = vehicles.find((x) => x.id === vehicleId);
              const fname =
                vehicleId === "__all"
                  ? `combined_expenses_${start}_${end}.xlsx`
                  : `${(v?.name || "vehicle").replace(/\s+/g, "_")}_expenses_${start}_${end}.xlsx`;
              await downloadFile(`/export/expenses?${params.toString()}`, fname);
              toast.success("Excel downloaded");
            } catch {
              toast.error("Download failed");
            }
          }}
          className="gap-2"
          data-testid="download-expenses-btn"
        >
          <Download className="h-4 w-4" /> Download Excel
        </Button>
      </div>

      <div className="card-swiss p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="label-eyebrow">Vehicle</Label>
            <Select value={vehicleId} onValueChange={setVehicleId}>
              <SelectTrigger className="mt-1" data-testid="expense-vehicle-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All vehicles</SelectItem>
                {vehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name} · {v.registration_number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow">Period</Label>
            <div className="mt-1">
              <PeriodSelector value={period} onChange={setPeriod} testid="expense-period" />
            </div>
          </div>
          <div>
            <Label className="label-eyebrow">Search</Label>
            <div className="relative mt-1">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                data-testid="expense-search"
                placeholder="Diesel, body emi, repair…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          label="Total Expenses"
          value={formatINR(total)}
          accent="text-red-600"
          testid="exp-total"
        />
        <StatCard
          label="Items Logged"
          value={count}
          sub={`${start} → ${end}`}
          testid="exp-count"
        />
        <StatCard
          label="Avg per Item"
          value={formatINR(count ? total / count : 0)}
          testid="exp-avg"
        />
      </div>

      {byCategory.length > 0 && (
        <div className="card-swiss p-5" data-testid="expense-categories">
          <div className="label-eyebrow mb-2">Top categories</div>
          <h3 className="font-display text-xl font-bold tracking-tight mb-4">
            By Remark
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {byCategory.map((c) => {
              const pct = total > 0 ? (c.total / total) * 100 : 0;
              return (
                <div
                  key={c.label}
                  className="border border-gray-200 rounded-md p-3"
                >
                  <div className="text-xs text-gray-500 truncate">{c.label}</div>
                  <div className="font-display text-xl font-bold mt-1">
                    {formatINR(c.total)}
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1">
                    {c.count} item{c.count !== 1 ? "s" : ""} · {pct.toFixed(1)}%
                  </div>
                  <div className="h-1 bg-gray-100 rounded-full mt-2 overflow-hidden">
                    <div
                      className="h-full bg-red-600"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="card-swiss p-5" data-testid="expense-list">
        <div className="flex items-center gap-2 mb-3">
          <Receipt className="h-4 w-4 text-gray-500" />
          <div className="label-eyebrow">All expense items</div>
        </div>
        {filtered.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-500">
            No expenses found in this range.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="py-2 pr-4 font-semibold">Date</th>
                  <th className="py-2 px-2 font-semibold">Vehicle</th>
                  <th className="py-2 px-2 font-semibold">Remark</th>
                  <th className="py-2 px-2 text-right font-semibold">Amount</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr
                    key={i}
                    className="border-b border-gray-100 hover-row"
                    data-testid={`expense-row-${i}`}
                  >
                    <td className="py-2 pr-4 font-mono text-gray-700">{r.date}</td>
                    <td className="py-2 px-2">
                      <div className="font-semibold">{r.vehicleName}</div>
                      <div className="text-[11px] text-gray-500 font-mono">
                        {r.vehicleReg}
                      </div>
                    </td>
                    <td className="py-2 px-2 text-gray-700">
                      {r.remark || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="py-2 px-2 text-right font-mono font-semibold text-red-600">
                      {formatINR(r.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300">
                  <td className="py-3 pr-4 font-bold uppercase tracking-wider text-xs" colSpan={3}>
                    Total
                  </td>
                  <td className="py-3 px-2 text-right font-mono font-bold text-red-600">
                    {formatINR(total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
